/**
 * CouponDetailModal - Presentation Component
 *
 * Modal displaying full coupon details: conditions, restrictions,
 * payment methods, shipping, product applicability.
 *
 * @see mobile: module-flash-sale/sales-rule/components/coupon-detail/
 */

'use client';

import React, { useCallback, useEffect, useMemo } from 'react';
import { Text, Button, Icon, Divider } from '@kidslabs/mario-nextjs-react-ui-core';
import type { CouponDetailModalProps } from './coupon-detail-modal.types';
import { getCouponImageUri } from '@kidslabs/module-flash-sale';
import styles from './coupon-detail-modal.module.css';

export const CouponDetailModal: React.FC<CouponDetailModalProps> = ({
    coupon,
    variant = 'voucher',
    showAddButton = true,
    isInWallet = false,
    isAdding = false,
    onClose,
    onAddToWallet,
}) => {
    const imageUri = useMemo(() => getCouponImageUri(coupon), [coupon]);

    const expiryDate = useMemo(() => {
        const toDate = coupon.salesrule?.to_date;
        if (!toDate) return '';
        try {
            return new Date(toDate).toLocaleDateString('vi-VN');
        } catch {
            return toDate;
        }
    }, [coupon.salesrule?.to_date]);

    const startDate = useMemo(() => {
        const fromDate = coupon.salesrule?.from_date;
        if (!fromDate) return '';
        try {
            return new Date(fromDate).toLocaleDateString('vi-VN');
        } catch {
            return fromDate;
        }
    }, [coupon.salesrule?.from_date]);

    // Close on Escape key
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    const handleBackdropClick = useCallback(
        (event: React.MouseEvent) => {
            if (event.target === event.currentTarget) {
                onClose();
            }
        },
        [onClose]
    );

    const buttonLabel = useMemo(() => {
        if (isInWallet && variant !== 'eWalletVoucher') return 'Đã lấy';
        if (variant === 'eWalletVoucher') return 'Đóng';
        return 'Lấy mã';
    }, [isInWallet, variant]);

    const isActionDisabled = isInWallet && variant !== 'eWalletVoucher';

    const detailSections = useMemo(
        () => [
            { label: 'Ưu đãi', value: coupon.description },
            {
                label: 'Thời gian sử dụng mã',
                value: startDate && expiryDate ? `${startDate} - ${expiryDate}` : expiryDate,
            },
            { label: 'Thiết bị', value: coupon.device },
            { label: 'Thanh toán', value: coupon.payment_method_text },
            { label: 'Đơn vị vận chuyển', value: coupon.shipping_method },
            { label: 'Sản phẩm', value: coupon.restriction },
        ],
        [coupon, startDate, expiryDate]
    );

    return (
        <div
            className={styles.overlay}
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-label="Chi tiết coupon"
        >
            <div className={styles.modal}>
                {/* Close button */}
                <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Đóng modal">
                    <Icon name="close" size={24} />
                </button>

                {/* Header - Coupon card preview */}
                <div className={styles.headerSection}>
                    <div
                        className={styles.headerImage}
                        style={{ backgroundColor: coupon.background_color ?? undefined }}
                    >
                        {imageUri ? (
                            <img
                                src={imageUri}
                                alt={coupon.name ?? 'Coupon'}
                                className={styles.brandImageLarge}
                                loading="lazy"
                            />
                        ) : (
                            <div className={styles.imagePlaceholderLarge}>
                                <Text size="xlarge" weight="bold" className={styles.placeholderTextLarge}>
                                    {coupon.name?.charAt(0) ?? 'C'}
                                </Text>
                            </div>
                        )}
                    </div>
                    <div className={styles.headerInfo}>
                        <Text variant="h3" size="large" weight="bold" className={styles.couponTitle}>
                            {coupon.name}
                        </Text>
                        {coupon.condition && (
                            <Text size="small" className={styles.couponCondition}>
                                {coupon.condition}
                            </Text>
                        )}
                        {coupon.salesrule?.code && (
                            <div className={styles.codeTag}>
                                <Text size="small" weight="bold" className={styles.codeText}>
                                    {coupon.salesrule.code}
                                </Text>
                            </div>
                        )}
                    </div>
                </div>

                <Divider />

                {/* Detail sections */}
                <div className={styles.detailContent}>
                    {detailSections.map(
                        section =>
                            section.value && (
                                <div key={section.label} className={styles.detailRow}>
                                    <Text size="small" weight="bold" className={styles.detailLabel}>
                                        {section.label}
                                    </Text>
                                    <Text size="small" className={styles.detailValue}>
                                        {section.value}
                                    </Text>
                                </div>
                            )
                    )}
                </div>

                {/* Footer action */}
                <div className={styles.footer}>
                    {showAddButton && variant !== 'eWalletVoucher' ? (
                        <Button
                            variant="contained"
                            colorRole="primary"
                            size="large"
                            disabled={isActionDisabled || isAdding}
                            onPress={isActionDisabled ? onClose : onAddToWallet}
                            className={styles.footerButton}
                        >
                            <Text size="medium" weight="bold" className={styles.footerButtonText}>
                                {buttonLabel}
                            </Text>
                        </Button>
                    ) : (
                        <Button
                            variant="outlined"
                            colorRole="primary"
                            size="large"
                            onPress={onClose}
                            className={styles.footerButton}
                        >
                            <Text size="medium" weight="bold" className={styles.footerButtonText}>
                                Đóng
                            </Text>
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

CouponDetailModal.displayName = 'CouponDetailModal';
